import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import apiClient from '@/api/client'
import type { AuthUser, CurrentUserResponse } from '@/types/auth.types'
import type { AccessLevel } from '@/types/auth.types'

/**
 * Backend `access_level` is already a namespaced key matching the frontend
 * union type, so no translation is needed — just validate against the known
 * set and fall back to the safest value.
 */
const VALID_ACCESS_LEVELS: AccessLevel[] = [
  'access_level.admin',
  'access_level.manager',
  'access_level.user',
]

function toAccessLevel(value: string | null): AccessLevel {
  return VALID_ACCESS_LEVELS.includes(value as AccessLevel)
    ? (value as AccessLevel)
    : 'access_level.user'
}

/**
 * Store for the currently authenticated user.
 * Fetches from GET /auth-user/me and provides both the full response
 * and a flat AuthUser shape for navigation/sidebar.
 */
export const useCurrentUserStore = defineStore('currentUser', () => {
  const user = ref<CurrentUserResponse | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  const detail = computed(() => user.value?.current_detail ?? null)

  /** Flat AuthUser shape for navigation guards and sidebar */
  const authUser = computed<AuthUser | null>(() => {
    if (!user.value) return null
    const d = user.value.current_detail
    return {
      id: user.value.id,
      email: user.value.email,
      first_name: user.value.first_name ?? '',
      last_name: user.value.last_name ?? '',
      access_level: toAccessLevel(user.value.access_level),
      picture_url: d?.picture_url ?? undefined,
      job_title: user.value.role_name ?? undefined,
    }
  })

  async function fetchCurrentUser() {
    loading.value = true
    error.value = null
    try {
      const { data } = await apiClient.get<CurrentUserResponse>('/auth-user/me')
      user.value = data
    } catch (e: unknown) {
      error.value = e instanceof Error ? e.message : 'Failed to load current user'
      throw e
    } finally {
      loading.value = false
    }
  }

  return { user, detail, authUser, loading, error, fetchCurrentUser }
})
